// ======================================================================
// 📁 File: src/modules/ppe/pages/resources/machine/MachineDrawer.jsx
// 📝 Purpose:
//   Machine Master – Create / Edit Drawer
//   - Machine group selection
//   - Capacity & flags
//   - Backend-driven lifecycle
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

// ----------------------------------------------------------------------
export default function MachineDrawer({
  open,
  mode,
  record,
  onClose,
  onSaved,
}) {
  const isEdit = mode === "edit";

  const [form, setForm] = useState({
    machine_group_id: "",
    code: "",
    name: "",
    capacity_per_shift_minutes: "",
    is_bottleneck: false,
    maintenance_required: false,
    is_active: true,
  });

  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  // ------------------------------------------------------------
  // Load Machine Groups (dropdown)
  // ------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/machine-groups")
      .then((res) => {
        setGroups(res.data?.data || []);
      })
      .catch(() => setGroups([]));
  }, []);

  // ------------------------------------------------------------
  // Populate edit data
  // ------------------------------------------------------------
  useEffect(() => {
    if (isEdit && record) {
      setForm({
        machine_group_id: record.machine_group_id || "",
        code: record.machine_code || "",
        name: record.machine_name || "",
        capacity_per_shift_minutes:
          record.capacity_per_shift_minutes || "",
        is_bottleneck: !!record.is_bottleneck,
        maintenance_required: !!record.maintenance_required,
        is_active: record.is_active,
      });
    }
  }, [isEdit, record]);

  // ------------------------------------------------------------
  // Handlers
  // ------------------------------------------------------------
  const update = (key, value) =>
    setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    // ✅ BASIC VALIDATION (SAFE)
    if (!form.machine_group_id || !form.code || !form.name) {
      alert("Machine Group, Code and Name are required");
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await axiosClient.put(
          `/api/ppe/masters/machines/${record.id}`,
          {
            machine_group_id: form.machine_group_id,
            code: form.code,
            name: form.name,
            capacity_per_shift_minutes:
              form.capacity_per_shift_minutes || null,
            is_bottleneck: form.is_bottleneck,
            maintenance_required: form.maintenance_required,
            is_active: form.is_active,
          }
        );
      } else {
        await axiosClient.post("/api/ppe/masters/machines", {
          machine_group_id: form.machine_group_id,
          code: form.code,
          name: form.name,
          capacity_per_shift_minutes:
            form.capacity_per_shift_minutes || null,
          is_bottleneck: form.is_bottleneck,
          maintenance_required: form.maintenance_required,
        });
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error("Machine save failed", err);
      alert("Failed to save machine");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="drawer-backdrop">
      <div className="drawer drawer-right">
        {/* ====================================================== */}
        {/* Header */}
        {/* ====================================================== */}
        <div className="drawer-header">
          <h5 style={{ margin: 0 }}>
            {isEdit ? "Edit Machine" : "Create Machine"}
          </h5>
          <button className="btn-close" onClick={onClose} />
        </div>

        {/* ====================================================== */}
        {/* Body */}
        {/* ====================================================== */}
        <div className="drawer-body">
          {/* Machine Group */}
          <div className="mb-3">
            <label className="form-label">Machine Group *</label>
            <select
              className="form-select"
              value={form.machine_group_id}
              onChange={(e) =>
                update("machine_group_id", e.target.value)
              }
            >
              <option value="">Select Machine Group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.group_code} – {g.group_name}
                </option>
              ))}
            </select>
          </div>

          {/* Code */}
          <div className="mb-3">
            <label className="form-label">Machine Code *</label>
            <input
              className="form-control"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => update("code", e.target.value)}
            />
          </div>

          {/* Name */}
          <div className="mb-3">
            <label className="form-label">Machine Name *</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>

          {/* Capacity */}
          <div className="mb-3">
            <label className="form-label">
              Capacity / Shift (minutes)
            </label>
            <input
              type="number"
              className="form-control"
              value={form.capacity_per_shift_minutes}
              onChange={(e) =>
                update(
                  "capacity_per_shift_minutes",
                  e.target.value
                )
              }
            />
          </div>

          {/* Flags */}
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={form.is_bottleneck}
              onChange={(e) =>
                update("is_bottleneck", e.target.checked)
              }
            />
            <label className="form-check-label">
              Bottleneck Machine
            </label>
          </div>

          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              checked={form.maintenance_required}
              onChange={(e) =>
                update(
                  "maintenance_required",
                  e.target.checked
                )
              }
            />
            <label className="form-check-label">
              Maintenance Required
            </label>
          </div>

          {isEdit && (
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) =>
                  update("is_active", e.target.checked)
                }
              />
              <label className="form-check-label">
                Active
              </label>
            </div>
          )}
        </div>

        {/* ====================================================== */}
        {/* Footer (MATCHES MachineGroupDrawer) */}
        {/* ====================================================== */}
        <div
          style={{
            padding: 16,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button
            className="btn btn-outline-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
